/**
 * What each tool is called and does, in one sentence — the copy behind a
 * shared link's preview card (worker/shellMeta.js rewrites the app shell's
 * <title>, description and Open Graph tags per tool from this) and the
 * generated card images in public/og/ (scripts/og-cards.mjs).
 *
 * Keyed by tool id, the same key as TOOL_PATHS in ./slugs.js. `family` is
 * the tool's group from src/solstack/lib/toolCatalog.js, which colours the
 * chip on the card image.
 *
 * Dependency-free on purpose: worker/index.js imports this too.
 */

export const TOOL_META = {
  // The tool grid at app.<domain>'s root — not a tool, but it's shared as
  // a link like one. No TOOL_PATHS entry: it lives at "/".
  app: {
    title: "Solstack Tools",
    description: "Create, launch, distribute, manage, lock and inspect Solana tokens from one place. Your keys stay in your wallet; fees shown before you sign.",
    family: "all",
  },
  "create-spl": {
    title: "SPL Token Creator",
    description: "Mint a standard SPL token with name, symbol and logo in one transaction, with optional authority revocation.",
    family: "create",
  },
  "create-spl22": {
    title: "Token-2022 Creator",
    description: "A Token-2022 mint with on-mint metadata and optional extensions: transfer tax, interest and more.",
    family: "create",
  },
  launchpad: {
    title: "Launchpad",
    description: "Presale or fair launch with escrowed tokens, automatic Raydium listing and burned or locked LP.",
    family: "launchpads",
  },
  launch: {
    title: "pump.fun Launcher",
    description: "Launch a coin on pump.fun with your first buy in the same transaction.",
    family: "launchpads",
  },
  "launch-bonk": {
    title: "letsbonk.fun Launcher",
    description: "Launch on Raydium LaunchLab, the curve behind letsbonk.fun, with an atomic first buy.",
    family: "launchpads",
  },
  "launch-meteora": {
    title: "Meteora DBC Launcher",
    description: "Launch on a Meteora Dynamic Bonding Curve with your first buy in the same transaction.",
    family: "launchpads",
  },
  stonkfun: {
    title: "StonkFun Launcher",
    description: "Launch a coin on StonkFun paired against SOL, a tokenized stock, another crypto or another coin.",
    family: "launchpads",
  },
  "creator-fees": {
    title: "Creator Fees",
    description: "Claim the creator fees your coins earn on pump.fun, LaunchLab and Meteora.",
    family: "launchpads",
  },
  "add-liquidity": {
    title: "Add Liquidity",
    description: "Find every pool that holds a token on Raydium, Meteora, Orca and PumpSwap, and deposit into one from your wallet.",
    family: "market",
  },
  "remove-liquidity": {
    title: "Remove Liquidity",
    description: "Find every LP token and liquidity position in your wallet on Raydium, Meteora, Orca and PumpSwap, and withdraw from any of them.",
    family: "market",
  },
  "grid-bot": {
    title: "Grid Trading Bot",
    description: "A bot that buys a token as its price falls through a ladder of lines and sells one line higher, from a wallet of its own.",
    family: "market",
  },
  "liquidity-bot": {
    title: "DLMM Liquidity Bot",
    description: "A bot that keeps a Meteora DLMM position around the price, earning swap fees and re-centring when the price moves away.",
    family: "market",
  },
  multisender: {
    title: "Multisender",
    description: "Send SOL or any token to a list of wallets from a CSV, batched into as few transactions as possible.",
    family: "distribute",
  },
  airdrop: {
    title: "Airdrop",
    description: "Send a token to every holder of another token, a flat amount each or split pro-rata.",
    family: "distribute",
  },
  manage: {
    title: "Token Manager",
    description: "Mint more supply, burn, revoke mint and freeze authorities, or update metadata for any mint you control.",
    family: "manage",
  },
  burn: {
    title: "Token Burner",
    description: "Destroy tokens from your wallet, permanently.",
    family: "manage",
  },
  "rent-reclaim": {
    title: "Rent Reclaim",
    description: "Close empty token accounts, burn dead tokens and NFTs, and recover the rent: about 0.002 SOL each.",
    family: "manage",
  },
  "wallet-cleaner": {
    title: "Wallet Cleaner",
    description: "Close empty accounts and burn dust or NFTs across throwaway wallets, then sweep their SOL to one address.",
    family: "manage",
  },
  // Runs on its own host (migration.<domain>); the /tools card links out.
  "evm-dust-cleaner": {
    title: "EVM Dust Cleaner",
    description: "Sell the tokens left in old EVM wallets on 15 chains and get them back as SOL on Solana. Wallets with no ETH for gas can still sell.",
    family: "manage",
  },
  "wallet-aggregator": {
    title: "Wallet Aggregator",
    description: "Collect one token, or every token, from many wallets into a single destination.",
    family: "distribute",
  },
  "token-locker": {
    title: "Token Locker",
    description: "Time-lock or vest any token in a program-owned vault, delivered automatically on schedule.",
    family: "locks",
  },
  "token-lock-verifier": {
    title: "Token Lock Verifier",
    description: "Every active lock on a mint: how much, for whom and until when.",
    family: "locks",
  },
  "lp-locker": {
    title: "LP Token Locker",
    description: "Lock LP tokens until a public unlock date anyone can verify.",
    family: "locks",
  },
  "lp-lock-verifier": {
    title: "LP Lock Verifier",
    description: "Is the LP locked on a mint, how much of it, and until when.",
    family: "locks",
  },
  snapshot: {
    title: "Holder Snapshot",
    description: "A CSV of every wallet holding a token or NFT collection.",
    family: "analyze",
  },
  "my-tokens": {
    title: "My Tokens",
    description: "Every token this wallet created, in one place.",
    family: "analyze",
  },
  "token-inspector": {
    title: "Token Inspector",
    description: "A 0–100 safety score plus authorities, LP lock, wallet forensics, liquidity and momentum for any mint.",
    family: "analyze",
  },
  "smart-money": {
    title: "Smart Money Tracker",
    description: "The top 100 holders of a token: how many trade profitably on other tokens, and how long they've held.",
    family: "analyze",
  },
  "stonkfun-rewards": {
    title: "StonkFun Rewards",
    description: "Which of a wallet's holdings are StonkFun reward coins, and roughly how much they've paid holders.",
    family: "analyze",
  },
  "tx-inspector": {
    title: "Transaction Inspector",
    description: "Status, fee, instructions and balance changes for any signature.",
    family: "analyze",
  },
  account: {
    title: "Account Inspector",
    description: "What any address is and what's on-chain about it.",
    family: "analyze",
  },
  status: {
    title: "Network Status",
    description: "Live TPS, priority fees, slot, epoch and RPC version.",
    family: "analyze",
  },
  sign: {
    title: "Sign & Verify",
    description: "Sign a message to prove wallet ownership, or verify a signature.",
    family: "utilities",
  },
  vanity: {
    title: "Vanity Address",
    description: "A keypair whose address starts or ends with your text, ground in your browser.",
    family: "utilities",
  },
  faucet: {
    title: "Devnet Faucet",
    description: "Test SOL on devnet or testnet.",
    family: "utilities",
  },
};

/** How many tools there are: every TOOL_META entry but the app grid itself.
 * Every "N tools" on the site and in the app reads this, so adding a tool
 * updates them all. */
export const TOOL_COUNT = Object.keys(TOOL_META).filter((id) => id !== "app").length;

export const TOOL_FAMILIES = {
  create: { label: "Create", color: "#ffd23f" },
  launchpads: { label: "Launchpads", color: "#c7f24c" },
  market: { label: "Liquidity", color: "#ff9ecd" },
  distribute: { label: "Distribute", color: "#5ad2f5" },
  manage: { label: "Manage", color: "#7ceb9a" },
  locks: { label: "Locks & vesting", color: "#ffa64d" },
  analyze: { label: "Analyze", color: "#c9a7ff" },
  utilities: { label: "Utilities", color: "#f2ede1" },
  all: { label: `${TOOL_COUNT} tools`, color: "#c7f24c" },
};
